import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';

import type {CampaignType} from 'nutshell-graphql-types';
import type {
    UnscheduleEdition as UnscheduleEditionMutation,
    UnscheduleEditionVariables as UnscheduleEditionMutationVariables,
    EditionUpdateInput,
    EditionStubFragment as EditionStub,
    EditionFragment as Edition,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {EditionContentPage} from './edition-content-page/edition-content-page';
import UNSCHEDULE_EMAIL_EDITION from './prepare-to-send-modal/graphql/mutations/unschedule-email-marketing-edition.graphql';
import {PrepareToSendPage} from './prepare-to-send-page/prepare-to-send-page';

export type ModalComponent = React.ComponentType<{
    isOpen: boolean;
    onClose: () => void;
}>;

type Props = ContextRouter & {
    onRemoveAllFilters: () => void;
    edition: Edition | null | undefined;
    editionStub: EditionStub | null | undefined;
    isLoading: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    isSaving: boolean;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onSetSelectedEditorTab: (arg1: string) => void;
    campaignType: CampaignType;
    generatePath: (arg1: string) => string;
};

export function EditionPage(props: Props) {
    const [isPreparingToSend, setIsPreparingToSend] = React.useState<boolean>(false);
    const [isScheduling, setIsScheduling] = React.useState<boolean>(false);
    const [isRescheduling, setIsRescheduling] = React.useState<boolean>(false);
    const [onboardingModalComponent, setOnboardingModalComponent] = React.useState<
        React.ReactNode | null | undefined
    >(null);

    const activeTab = props.match.params.tab || 'details';

    const onSetSelectedEditorTab = props.onSetSelectedEditorTab;
    const sentStatuses = [
        'SENDING',
        'SENT',
        'HALTED',
        'IN_REVIEW',
        'COMPARISON_SENDING',
        'COMPARISON_WAITING',
    ];
    const isEditionSent = props.edition && sentStatuses.indexOf(props.edition.status) !== -1;
    const isEditionScheduled = props.edition && props.edition.status === 'SCHEDULED';

    React.useEffect(() => {
        if (activeTab === 'editor' && isEditionSent) {
            onSetSelectedEditorTab('engagement');
        }
    }, [activeTab, isEditionSent, onSetSelectedEditorTab]);

    React.useEffect(() => {
        if (
            activeTab === 'editor' &&
            isEditionScheduled &&
            NutClientConfig.hasNewAudienceManagementNUT14721
        ) {
            onSetSelectedEditorTab('details');
        }
    }, [activeTab, isEditionScheduled, onSetSelectedEditorTab]);

    const onPrepareToSendClose = (
        editorTab?: string | null,
        onboardingComponent?: ModalComponent | null
    ) => {
        if (editorTab) {
            onSetSelectedEditorTab(editorTab);
        } else {
            onSetSelectedEditorTab('details');
        }

        // Occasionally the primary action from the PrepareToSend modal
        // is a "Show me how", in which we'll pop an onboarding modal
        if (onboardingComponent) {
            const OnboardingModal = onboardingComponent;
            // Wait a second for the user to get oriented with a previously
            // closed modal
            setTimeout(() => {
                setOnboardingModalComponent(
                    <OnboardingModal
                        isOpen={true}
                        onClose={() => setOnboardingModalComponent(null)}
                    />
                );
            }, 1000);
        }

        // Without this, scheduling state persists after unscheduling (if you schedule and
        // then unschedule and then open the prepare to send page again without refreshing,
        // the schedule breadcrumb is still there)
        setIsScheduling(false);
        setIsRescheduling(false);
    };

    const [unscheduleEdition] = useMutation<
        UnscheduleEditionMutation,
        UnscheduleEditionMutationVariables
    >(UNSCHEDULE_EMAIL_EDITION);

    return NutClientConfig.hasNewAudienceManagementNUT14721 &&
        props.edition &&
        activeTab === 'prepare' ? (
        <PrepareToSendPage
            onClose={onPrepareToSendClose}
            onSendSuccess={() => props.onSetSelectedEditorTab('engagement')}
            edition={props.edition}
            type={props.campaignType}
            isEditionLoading={props.isLoading || props.isSaving}
            isScheduling={isScheduling}
            isRescheduling={isRescheduling}
            setIsScheduling={(value) => setIsScheduling(value)}
            setIsRescheduling={(value) => setIsRescheduling(value)}
            onUnschedule={
                props.edition
                    ? () => {
                          return unscheduleEdition({
                              variables: {
                                  edition: {
                                      editionId: props.edition.id,
                                  },
                              },
                          });
                      }
                    : undefined
            }
        />
    ) : (
        <>
            <EditionContentPage
                {...props}
                activeTab={activeTab}
                isPreparingToSend={isPreparingToSend}
                // @ts-expect-error
                setIsPreparingToSend={(value) => setIsPreparingToSend(value)}
                // @ts-expect-error
                setIsScheduling={(value) => setIsScheduling(value)}
                // @ts-expect-error
                setIsRescheduling={(value) => setIsRescheduling(value)}
                isScheduling={isScheduling}
                isRescheduling={isRescheduling}
                setOnboardingModalComponent={(component?: React.ReactNode | null) =>
                    setOnboardingModalComponent(component)
                }
            />
            {onboardingModalComponent || undefined}
        </>
    );
}

/**
 * Get the styles for the BEE container. We show a static BEE editor, and need
 * to change the styles of the wrapper to ensure everything shows correctly. See
 * comment above about why we only show a single instance of the editor.
 *
 * @param {?string} activeTab The tab the user is currently viewing.
 */
export function getBeeEditorStyle(activeTab?: string | null) {
    if (activeTab === 'editor') {
        return {width: '100%', height: '100%'};
    } else if (activeTab === 'details') {
        return {
            width: '70%',
            height: '100%',
            boxSizing: 'border-box',
        };
    }

    return {display: 'none'};
}
